/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IDobbiesCheckoutOrderSummaryViewProps } from './dobbies-checkout-order-summary';

import { ILineItem, ILineItemDeliveryGroup, ILineItems, IPickUpAtStore } from '@msdyn365-commerce-modules/checkout';
import { getCartState, getCheckoutState, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
import {
    CartLine,
    ProductDeliveryOptions,
    ProductSearchCriteria,
    ProductSearchResult,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';

import {
    generateImageUrl,
    getFallbackImageUrl,
    getInvoiceDetailsPageUrlSync,
    getProductUrlSync,
    IFullOrgUnitAvailability,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getByIdsAsync,
    getDeliveryOptionsAsync,
    searchByCriteriaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getAllOrgUnitLocationsByArea, GetAllOrgUnitLocationsByAreaInput } from '../../actions/get-all-store-location-information';
import { ICommerceApiSettings, IGridSettings, IImageData, IImageSettings } from '@msdyn365-commerce/core-internal';
import MsDyn365, { Image } from '@msdyn365-commerce/core';
import { getDelayedDeliveryMsg, getDeliveryDateRange } from '../../themes/dobbies/utilities/estDeliveryDate';
import Button from 'reactstrap/lib/Button';
import PriceComponent from '../../themes/dobbies/views/components/price.component';
export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <>
        {label}
        {location}
    </>
);

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore }) => {
    return (
        <>
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        </>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ heading, lineItems }) => (
    <>
        {heading}
        {lineItems.map(lineItem => (
            <LineItemComponent key={lineItem.LineId} {...lineItem} />
        ))}
    </>
);

export const LineItemsComponent: React.FC<ILineItems> = ({ heading, editLink, itemsForPickup, itemsForShip }) => (
    <>
        {/* {heading}
        {editLink} */}
        {itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
    </>
);

export default (props: IDobbiesCheckoutOrderSummaryViewProps) => {
    const [cncCartlines, setCnCCartlines] = React.useState<CartLine[]>([]);
    const [homeDelCartlines, setHomeDelCartlines] = React.useState<CartLine[]>([]);
    const [simpleProducts, setSimpleProducts] = React.useState<SimpleProduct[]>([]);
    const [allStores, setAllStores] = React.useState<IFullOrgUnitAvailability[]>();
    const [productsWithAttributes, setProductsWithAttributes] = React.useState<ProductSearchResult[]>([]);
    const orgUnitLocationsInput = new GetAllOrgUnitLocationsByAreaInput(31.5070767, 74.4315609, 0, 0, false);
    const [deliveryCharges, setDeliveryCharges] = React.useState(0);
    const [totalCost, setCartTotalCost] = React.useState(0);
    const [totalItems, setTotalItems] = React.useState(0);
    const [productDeliveryOptions, setProductDeliveryOptions] = React.useState<ProductDeliveryOptions[]>([]);
    const [checkoutCartState, setCheckoutCartState] = React.useState<ICheckoutState | undefined>(undefined);
    const [cartState, setCartState] = React.useState<ICartState | undefined>();
    const [delCharges, setDelCharges] = React.useState<number>(0);

    React.useEffect(() => {
        getCheckoutState(props.context.actionContext)
            .then(res => {
                const cart = res.checkoutCart.cart;
                setCheckoutCartState(res);
                setCartTotalCost(cart.TotalAmount || 0);
                setTotalItems(cart.TotalItems || 0);
                const cncLines = cart.CartLines?.filter(cl => (cl.FulfillmentStoreId ? true : false));
                const homeDelLines = cart.CartLines?.filter(cl => (!cl.FulfillmentStoreId ? true : false));
                setCnCCartlines(cncLines || []);
                setHomeDelCartlines(homeDelLines || []);
                const prodIds = cart.CartLines?.map(cl => cl.ProductId);
                const validProdIds: number[] = [];
                getAllOrgUnitLocationsByArea(orgUnitLocationsInput, props.context.actionContext).then(stores => {
                    setAllStores(stores);
                });
                prodIds?.map(prodId => {
                    if (prodId) {
                        validProdIds.push(prodId);
                    }
                });
                getByIdsAsync(
                    {
                        callerContext: props.context.actionContext,
                        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(props.context.actionContext)
                    },
                    props.context.request.apiSettings.channelId,
                    validProdIds,
                    null,
                    props.context.request.apiSettings.catalogId ?? 0
                )
                    .then(async res => {
                        setSimpleProducts(res);
                        const searchCriteriaInput: ProductSearchCriteria = {};
                        searchCriteriaInput.Context = {
                            ChannelId: props.context.actionContext.requestContext.apiSettings.channelId,
                            CatalogId: props.context.actionContext.requestContext.apiSettings.catalogId
                        };
                        searchCriteriaInput.Ids = validProdIds;
                        searchCriteriaInput.IncludeAttributes = true;
                        const actionContext = props.context.actionContext;
                        const productsWithAttributes = await searchByCriteriaAsync(
                            { callerContext: actionContext, queryResultSettings: {} },
                            searchCriteriaInput
                        );
                        setProductsWithAttributes(productsWithAttributes);
                    })
                    .catch(e => {});
                getDeliveryOptionsAsync(
                    { callerContext: props.context.actionContext, queryResultSettings: {} },
                    validProdIds ? validProdIds : []
                )
                    .then((deliveryOptions: ProductDeliveryOptions[]) => {
                        setProductDeliveryOptions(deliveryOptions);
                    })
                    .catch(e => {});
            })
            .catch(e => {});

        getCartState(props.context.actionContext).then(cartState => {
            setCartState(cartState);
            // let deliveryCharges = 0;
            // cartState.cart.ChargeLines?.map(chargeLine => {
            //     deliveryCharges += chargeLine.CalculatedAmount || 0;
            // });
            // setDelCharges(deliveryCharges);
        });
    }, []);

    // Once charges have been updated, calculate total delivery charges
    React.useEffect(() => {
        if (props.data.checkoutModulesSharedState.result?.isChargesUpdated) {
            getCheckoutState(props.context.actionContext).then(checkoutState => {
                let charges = 0;
                checkoutState.checkoutCart?.cart.ChargeLines?.map(chargeLine => {
                    charges += chargeLine.CalculatedAmount || 0;
                });
                setDelCharges(charges);
                setDeliveryCharges(charges);
            });
        }
    }, [props.data.checkoutModulesSharedState.result]);

    const totalQtyOfHD =
        homeDelCartlines?.reduce((total: any, item: any) => {
            return total + (item.Quantity || 0);
        }, 0) ?? 0;

    const totalQtyOfCNC =
        cncCartlines?.reduce((total: any, item: any) => {
            return total + (item.Quantity || 0);
        }, 0) ?? 0;
    const getProductPlacementImage = (
        imageSettings?: IImageSettings,
        gridSettings?: IGridSettings,
        imageUrl?: string,
        altText?: string,
        apiSettings?: ICommerceApiSettings,
        fallbackImageUrl?: string
    ) => {
        if (!imageUrl || !gridSettings || !imageSettings || !apiSettings) {
            return null;
        }

        const newImageUrl = generateImageUrl(imageUrl, apiSettings);

        const img: IImageData = {
            src: newImageUrl ? newImageUrl : '',
            altText: altText ? altText : ''
        };
        const imageProps = {
            gridSettings: gridSettings,
            imageSettings: imageSettings
        };
        imageProps.imageSettings.cropFocalRegion = true;

        // debugger;
        return <Image {...img} {...imageProps} loadFailureBehavior='empty' fallBackSrc={fallbackImageUrl} />;
    };
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=118&h=165&m=6', w: 118, h: 165 },
            lg: { q: 'w=118&h=165&m=6', w: 118, h: 165 },
            xl: { q: 'w=118&h=165&m=6', w: 118, h: 165 }
        },
        lazyload: true
    };

    const freeDelivery: number = props.config.freeDeliveryMilestone!;
    let spendX: number | Boolean = freeDelivery - checkoutCartState?.checkoutCart.cart.SubtotalAmount!;
    let numWithZeroes;
    if (spendX < 0 || delCharges === props.context.request.app.config.chargesForHDItems) {
        spendX = false;
    } else {
        numWithZeroes = parseFloat(spendX.toString()).toFixed(2);
    }

    const cncItems = checkoutCartState?.checkoutCart?.cart.CartLines?.filter(
        line => line.DeliveryMode === props.context.request.channel?.PickupDeliveryModeCode
    );
    const isCNCOnlyCart = cncItems && cncItems?.length === checkoutCartState?.checkoutCart.cart.CartLines?.length;

    const hasHDItem = props.data.deliveryOptions.result?.some(option =>
        option.DeliveryOptions?.some(del => del.Code === 'HD' || del.Code === 'HD Presale')
    );
    const hasSTD2Item = props.data.deliveryOptions.result?.some(option => option.DeliveryOptions?.some(del => del.Code === 'STD-2'));
    // const cartDeliveryModes = cartState?.cart.CartLines?.map(line => line.DeliveryMode).filter(
    //     mode => mode !== 'Common MOD' && mode !== 'ClickCol'
    // );

    // const isOnlyHDCart = cartDeliveryModes?.every(mode => mode === 'HD' || mode === 'HD Presale');

    // const isMixedCart =
    //     cartDeliveryModes?.some(mode => mode === 'HD' || mode === 'HD Presale') &&
    //     cartDeliveryModes?.some(mode => mode !== 'HD' && mode !== 'HD Presale');
    const additionalMsgConfig = props.context.actionContext.requestContext.app.config.additionalDeliveryMessages;
    const HDDeliveryCharges =
        delCharges !== undefined ? (delCharges === 0 ? 'Free' : props.context.cultureFormatter.formatCurrency(delCharges)) : undefined;

    let deliveryMessage: string | null = null;
    const deliveryModes = cartState?.cart.CartLines?.map(line => line.DeliveryMode?.trim()).filter(Boolean);
    const LARGE_DELIVERY_MODES = ['HD', 'HD Presale'];
    const IGNORED_MODES = ['Clickcol', 'Common MOD'];
    const equalsIgnoreCase = (a: string, b?: string) => a?.toLowerCase() === b?.toLowerCase();
    const relevantModes = deliveryModes?.filter(mode => !IGNORED_MODES.some(ignore => equalsIgnoreCase(ignore, mode)));
    const uniqueModes = [...new Set(relevantModes?.map(m => m?.toLowerCase()))];
    const hasHD = uniqueModes.some(mode => LARGE_DELIVERY_MODES.some(large => equalsIgnoreCase(large, mode)));
    const hasSTD2 = uniqueModes.some((mode: any) => equalsIgnoreCase(mode, 'STD-2'));
    const isOnlyStdAndPf = uniqueModes.every((mode: any) => equalsIgnoreCase(mode, 'STD') || equalsIgnoreCase(mode, 'PF'));
    const isMixed = uniqueModes.length > 1 && !isOnlyStdAndPf;
    if (isMixed) {
        deliveryMessage = 'Mixed delivery methods.';
    } else if (hasHD) {
        deliveryMessage = 'XL item delivery.';
    } else if (hasSTD2) {
        deliveryMessage = 'Large item delivery.';
    }
    let cartDeliveryCharges = 0;
    props.data.checkout.result?.checkoutCart.cart.ChargeLines?.forEach(chargeLine => {
        cartDeliveryCharges += chargeLine.CalculatedAmount || 0;
    });

    return (
        <div className='row'>
            <div className='ms-checkout__line-items multiple-pickup-enabled'>
                <div className='ms-checkout__line-items-header'>
                    <a
                        title='Edit Cart'
                        href='/cart'
                        data-exp-event-id='Checkout.Checkout.Edit cart.click'
                        className='ms-checkout__line-items-edit-cart-link msc-btn btn-link'
                    >
                        Go to basket
                    </a>
                </div>
                {homeDelCartlines.length > 0 && (
                    <div className='ms-checkout__line-items-delivery-group ship'>
                        {/* removed count from title */}
                        <div className='ms-checkout__line-items-group-title'>Home Delivery</div>
                        {!isCNCOnlyCart && (
                            <div className='ms-checkout__line-items-group-count-cost'>
                                {/* make dynamic */}
                                <span>{totalQtyOfHD === 1 ? `${totalQtyOfHD} item` : `${totalQtyOfHD} items`}</span>
                                <span>
                                    Delivery cost:{' '}
                                    {hasHDItem || hasSTD2Item
                                        ? `${HDDeliveryCharges}`
                                        : deliveryCharges === 0
                                        ? 'Free'
                                        : `£${deliveryCharges}`}
                                </span>
                            </div>
                        )}
                        <div className='ms-checkout__line-items-list'>
                            {homeDelCartlines.map(homeDelCl => {
                                const additionalMsgForDel = additionalMsgConfig?.find(
                                    (delMsgForMode: { delMode: string; msg: string }) => delMsgForMode.delMode === homeDelCl.DeliveryMode
                                )?.msg;
                                const matchingProd = simpleProducts.find(sp => sp.RecordId === homeDelCl.ProductId);
                                let productUrl: string | undefined;
                                if (matchingProd) {
                                    productUrl = getProductUrlSync(matchingProd, props.context.actionContext, undefined);

                                    if (MsDyn365.isBrowser) {
                                        const fullUrl = new URL(productUrl, window.location.href);
                                        fullUrl.searchParams.set('catalogid', `${homeDelCl.CatalogId ?? 0}`);
                                        productUrl = fullUrl.href;
                                    }
                                } else {
                                    productUrl = getInvoiceDetailsPageUrlSync(homeDelCl.Description || '', props.context.actionContext);
                                }
                                const deliveryModeForCartlineInfo = productDeliveryOptions
                                    .find(delOpt => delOpt.ProductId === homeDelCl.ProductId)
                                    ?.DeliveryOptions?.find(
                                        dopt =>
                                            dopt.Code !== props.context.request.channel?.PickupDeliveryModeCode &&
                                            dopt.Code !== 'Common MOD'
                                    );
                                const estDeliveryDate = getDeliveryDateRange(
                                    new Date(),
                                    props.context.actionContext,
                                    deliveryModeForCartlineInfo?.Code || ''
                                );
                                const delayedDelMsg = props.data.checkout.result?.shippingAddress?.ZipCode
                                    ? getDelayedDeliveryMsg(
                                          props.context.actionContext,
                                          props.data.checkout.result?.shippingAddress?.ZipCode
                                      )
                                    : '';
                                const prodWithAtt = productsWithAttributes.find(pwa => pwa.RecordId === matchingProd?.RecordId);
                                const vendorNameAttr = prodWithAtt?.AttributeValues?.find(attr => {
                                    return attr.Name?.toLowerCase() === 'vendorname';
                                });
                                const vendorName = vendorNameAttr && vendorNameAttr.TextValue;
                                const eachPrice = ((homeDelCl.ExtendedPrice || 0) / (homeDelCl.Quantity || 1)).toFixed(2);
                                const fallbackImageUrl = getFallbackImageUrl(
                                    matchingProd?.ItemId,
                                    props.context.actionContext.requestContext.apiSettings,
                                    props.context.request.app?.config?.OmniChannelMedia
                                );
                                return (
                                    <div className='ms-checkout__line-item' key={homeDelCl.LineId || homeDelCl.ProductId}>
                                        <div className='msc-cart-line'>
                                            <div className='msc-cart-line__product-image'>
                                                <a href={productUrl} target='_blank' rel='noreferrer'>
                                                    {getProductPlacementImage(
                                                        defaultImageSettings,
                                                        props.context.request.gridSettings,
                                                        matchingProd?.PrimaryImageUrl,
                                                        '',
                                                        props.context.request.apiSettings,
                                                        fallbackImageUrl
                                                    )}
                                                </a>
                                            </div>
                                            <div className='msc-cart-line__content'>
                                                <div className='msc-cart-line__product'>
                                                    <label className=''>Item: </label>
                                                    <a
                                                        className='msc-cart-line__product-title'
                                                        href={productUrl}
                                                        data-exp-event-id='Checkout.Checkout.5638322860.click'
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        {matchingProd?.Name}
                                                    </a>
                                                    {/* <div className='msc-cart-line-item-product-discount'>
                                                <span className='msc-cart-line__product-savings-label'>50% Sale - Plants: Savings </span>
                                                <span className='msc-cart-line__promo-codes'>
                                                    <span className='msc-price msc-cart-line__discount-value'>
                                                        <span className='msc-price__actual ' itemprop='price'>
                                                            £12.99
                                                        </span>
                                                    </span>
                                                </span>
                                                <span> (50%)</span>
                                            </div> */}
                                                    <PriceComponent
                                                        data={{
                                                            price: {
                                                                CustomerContextualPrice: homeDelCl.ExtendedPrice,
                                                                BasePrice: (homeDelCl.Price || 0) * (homeDelCl.Quantity || 1)
                                                            }
                                                        }}
                                                        context={props.context}
                                                        id={homeDelCl.LineId || ''}
                                                        typeName={'order-summart'}
                                                        className='discount-value'
                                                        originalPriceText={'Original price'}
                                                        currentPriceText={'Current price'}
                                                        shouldNotShowWasText={true}
                                                    />
                                                    <div className='msc-cart-line__quantity'>
                                                        <label className='quantity-label'>Quantity:</label>
                                                        <span className='quantity-value'>
                                                            {homeDelCl.Quantity} - £{eachPrice} each
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* make it dynamic */}
                                        <div className='msc-cart-line__delivery-detail'>
                                            <p>Dispatched by {vendorName ? vendorName : 'Dobbies'}</p>
                                            <p>Delivery: {deliveryModeForCartlineInfo?.Description}</p>

                                            {additionalMsgForDel ? (
                                                <p>
                                                    <span>{additionalMsgForDel}</span>
                                                </p>
                                            ) : (
                                                <></>
                                            )}
                                            {homeDelCl.DeliveryMode !== 'HD' && homeDelCl.DeliveryMode !== 'HD Presale' && (
                                                <>
                                                    <p>Estimated arrival by: {estDeliveryDate?.maxDeliveryDate}</p>
                                                    <p className='delivery-note'>{` ${delayedDelMsg}`}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {cncCartlines.length > 0 && (
                    <div className='ms-checkout__line-items-delivery-group pickup'>
                        {/* removed count from title */}
                        <div className='ms-checkout__line-items-group-title'>Click & Collect</div>
                        <div className='ms-checkout__line-items-group-count-cost'>
                            {/* make dynamic */}
                            <span>{totalQtyOfCNC === 1 ? `${totalQtyOfCNC} item` : `${totalQtyOfCNC} items`}</span>
                            <span>Collection cost: Free</span>
                        </div>
                        {/* Static text */}
                        <div className='ms-checkout__line-items-group-text'>
                            <p>Ready within 24 hours if you order before 4pm.</p>
                            <p>
                                We’ll email when your order is ready. {''}
                                <a href={props.context.app.config.cartLineLearnMoreLink || '#'} target='_blank' rel='noreferrer'>
                                    Learn more
                                </a>
                            </p>
                        </div>
                        <div className='ms-checkout__line-items-list'>
                            {cncCartlines?.map(cncCl => {
                                const matchingProd = simpleProducts.find(sp => sp.RecordId === cncCl.ProductId);
                                let productUrl: string | undefined;
                                if (matchingProd) {
                                    productUrl = getProductUrlSync(matchingProd, props.context.actionContext, undefined);

                                    if (MsDyn365.isBrowser) {
                                        const fullUrl = new URL(productUrl, window.location.href);
                                        fullUrl.searchParams.set('catalogid', `${cncCl.CatalogId ?? 0}`);
                                        productUrl = fullUrl.href;
                                    }
                                } else {
                                    productUrl = getInvoiceDetailsPageUrlSync(cncCl.Description || '', props.context.actionContext);
                                }
                                const eachPrice = ((cncCl.ExtendedPrice || 0) / (cncCl.Quantity || 1)).toFixed(2);
                                const fallbackImageUrl = getFallbackImageUrl(
                                    matchingProd?.ItemId,
                                    props.context.actionContext.requestContext.apiSettings,
                                    props.context.request.app?.config?.OmniChannelMedia
                                );
                                return (
                                    <div className='ms-checkout__line-item' key={cncCl.LineId || cncCl.ProductId}>
                                        <div className='msc-cart-line'>
                                            <div className='msc-cart-line__product-image'>
                                                <a href={productUrl} target='_blank' rel='noreferrer'>
                                                    {getProductPlacementImage(
                                                        defaultImageSettings,
                                                        props.context.request.gridSettings,
                                                        matchingProd?.PrimaryImageUrl,
                                                        '',
                                                        props.context.request.apiSettings,
                                                        fallbackImageUrl
                                                    )}
                                                </a>
                                            </div>
                                            <div className='msc-cart-line__content'>
                                                <div className='msc-cart-line__product'>
                                                    <a
                                                        className='msc-cart-line__product-title'
                                                        href={productUrl}
                                                        data-exp-event-id={`Checkout.Checkout.${matchingProd?.RecordId}.click`}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        {matchingProd?.Name}
                                                    </a>
                                                    <PriceComponent
                                                        data={{
                                                            price: {
                                                                CustomerContextualPrice: cncCl.ExtendedPrice,
                                                                BasePrice: (cncCl.Price || 0) * (cncCl.Quantity || 1)
                                                            }
                                                        }}
                                                        context={props.context}
                                                        id={cncCl.LineId || ''}
                                                        typeName={'order-summart'}
                                                        className='discount-value'
                                                        originalPriceText={'Original price'}
                                                        currentPriceText={'Current price'}
                                                        shouldNotShowWasText={true}
                                                    />
                                                    <div className='msc-cart-line__quantity single-quantity'>
                                                        <label className='quantity-label'>Quantity:</label>
                                                        <span className='quantity-value'>
                                                            {cncCl.Quantity} - £{eachPrice} each
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='ms-checkout__pick-up-at-store'>
                                            <span className='ms-checkout__store-label'>In stock:</span>
                                            <div className='ms-checkout__store-location'>
                                                {`Dobbies ${allStores?.find(
                                                    store =>
                                                        store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber ===
                                                        cncCl.FulfillmentStoreId
                                                )?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName || ''}`}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            <div className='flex-fill msc-order-summary-wrapper'>
                <div className='msc-order-summary__content'>
                    <h2 className='msc-order-summary__heading'>
                        Order summary{' '}
                        <span className='msc-order-summary__heading-items-count'>
                            {' '}
                            ({totalItems} {totalItems && totalItems !== 1 ? 'items' : 'item'})
                        </span>
                    </h2>
                    <div className='msc-order-summary__items'>
                        <p className='msc-order-summary__line-sub-total'>
                            <span className='msc-order-summary__label'>Subtotal: </span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual' itemProp='price'>
                                    {checkoutCartState?.checkoutCart.cart.SubtotalAmount !== undefined &&
                                        props?.context?.cultureFormatter.formatCurrency(
                                            checkoutCartState?.checkoutCart.cart.SubtotalAmount
                                        )}
                                </span>
                            </span>
                        </p>
                        <>
                            {!isCNCOnlyCart && (
                                <div className='msc-order-summary__line-delivery-cost'>
                                    <p>
                                        <span className='msc-order-summary__label'>Delivery cost:</span>
                                        <span className='msc-order-summary__value'>
                                            {hasHDItem || hasSTD2Item
                                                ? `${HDDeliveryCharges}`
                                                : (checkoutCartState?.checkoutCart?.cart?.SubtotalAmount &&
                                                      checkoutCartState?.checkoutCart?.cart?.SubtotalAmount > freeDelivery) ||
                                                  cartDeliveryCharges === 0
                                                ? 'Free'
                                                : `£${cartDeliveryCharges}`}
                                        </span>
                                    </p>

                                    {/* {hasHDItem && isOnlyHDCart ? <p className='large-item-label'>Large item delivery.</p> : <></>}
                                    {hasHDItem && isMixedCart ? <p className='large-item-label'>Mixed delivery methods.</p> : <></>} */}

                                    {deliveryMessage && <p className='large-item-label'>{deliveryMessage}</p>}

                                    {spendX && !isCNCOnlyCart ? (
                                        <div className='promptSpend'>
                                            Spend £{numWithZeroes} more for free delivery. Eligible items only.
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    {!spendX && !hasHDItem && !isCNCOnlyCart ? (
                                        <p className='promptSpend'>You have qualified for free delivery.</p>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            )}
                        </>
                        {cncCartlines.length > 0 ? (
                            <>
                                <div className='msc-order-summary__line-delivery-cost'>
                                    <p>
                                        <span className='msc-order-summary__label'>Click & Collect:</span>
                                        <span className='msc-order-summary__value'>Free</span>
                                    </p>
                                </div>
                            </>
                        ) : (
                            <></>
                        )}{' '}
                        <p className='msc-order-summary__line-total'>
                            <span className='msc-order-summary__label'>Total</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual' itemProp='price'>
                                    {totalCost !== undefined && props?.context?.cultureFormatter.formatCurrency(totalCost)}
                                </span>
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            <Button
                onClick={() => {
                    props.onSave();
                }}
                className='msc-btn msc-btn-default msc-cart__btn-guestcheckout'
            >
                Save and continue
            </Button>
        </div>
    );
};
